import { Tag, Button, Dropdown, Tooltip, Switch } from 'antd';
import { EditOutlined, DeleteOutlined, MoreOutlined } from '@ant-design/icons';
import { NavigateFunction } from 'react-router-dom';
import { EditPackageRoute } from '../../routes/routepath';

/* ─── Types ──────────────────────────────────────────────────────────── */

export interface PackageRecord {
  _id?:         string;
  name?:        string;
  type?:        string;
  pricing?:     number;
  numberOfDays?: number;
  freezable?:   boolean;
  status?:      string;
  branchIds?:   { name?: string }[];
  branchId?:    { name?: string };
}

/* ─── Columns factory ────────────────────────────────────────────────── */

export const getPackageColumns = (
  handleDelete: (record: PackageRecord) => void,
  navigate:     NavigateFunction,
  canEdit      = false,
  canDelete    = false,
  handleToggle?: (record: PackageRecord) => void,
  canToggle    = false,
  togglingId?: string | null,
) => [
  {
    title:     'Package Type',
    dataIndex: 'type',
    key:       'packageType',
    width:     150,
    render:    (type: string) =>
      type ? type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ') : '-',
  },
  {
    title: 'Branch Name',
    key:   'branchName',
    width: 200,
    render: (_: unknown, record: PackageRecord) => {
      if (record.branchIds && Array.isArray(record.branchIds) && record.branchIds.length > 0) {
        if (record.branchIds.length === 1) return record.branchIds[0]?.name || '-';
        const branchNames = record.branchIds.map(b => b?.name).filter(Boolean) as string[];
        return (
          <Tooltip
            title={<div>{branchNames.map((n, i) => <div key={i}>{n}</div>)}</div>}
            placement="topLeft"
          >
            <span style={{ cursor: 'pointer' }}>{branchNames[0]} (+{branchNames.length - 1} more)</span>
          </Tooltip>
        );
      }
      if (record.branchId) return record.branchId?.name || '-';
      return '-';
    },
  },
  {
    title:     'Package Name',
    dataIndex: 'name',
    key:       'packageName',
    width:     200,
  },
  {
    title:     'Package Pricing(₹)',
    dataIndex: 'pricing',
    key:       'pricing',
    width:     150,
    render:    (price: number) => price ? `₹${price}` : '-',
  },
  {
    title:     'Number Of Days',
    dataIndex: 'numberOfDays',
    key:       'numberOfDays',
    width:     130,
    render:    (days: number) => days || '-',
  },
  {
    title:     'Freezable',
    dataIndex: 'freezable',
    key:       'freezable',
    width:     100,
    align:     'center' as const,
    render:    (freezable: boolean) => (
      <Tag color={freezable ? 'green' : 'red'}>{freezable ? 'YES' : 'NO'}</Tag>
    ),
  },
  {
    title:     'Status',
    dataIndex: 'status',
    key:       'status',
    width:     100,
    align:     'center' as const,
    render:    (status: string) => (
      <Tag color={status === 'active' ? 'green' : 'red'}>
        {status === 'active' ? 'ACTIVE' : 'INACTIVE'}
      </Tag>
    ),
  },
  ...(canToggle ? [{
    title:  'Toggle',
    key:    'toggle',
    width:  80,
    align:  'center' as const,
    render: (_: unknown, record: PackageRecord) => (
      <Switch
      size='small'
        checked={record.status === 'active'}
        loading={togglingId === record._id}
        onChange={() => handleToggle?.(record)}
        style={{ backgroundColor: record.status === 'active' ? '#52c41a' : undefined }}
      />
    ),
  }] : []),
  {
    title:  'Actions',
    key:    'actions',
    width:  80,
    align:  'center' as const,
    render: (_: unknown, record: PackageRecord) => {
      const menuItems = [
        ...(canEdit ? [{
          key:     'edit',
          label:   'Edit',
          icon:    <EditOutlined />,
          onClick: () => navigate(`${EditPackageRoute}/${record._id}`),
        }] : []),
        ...(canDelete ? [{
          key:     'delete',
          label:   'Delete',
          icon:    <DeleteOutlined />,
          danger:  true,
          onClick: () => handleDelete(record),
        }] : []),
      ];
      if (menuItems.length === 0) return null;
      return (
        <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
          <Button type="text" icon={<MoreOutlined style={{ fontSize: 20 }} />} />
        </Dropdown>
      );
    },
  },
];
